import { Alert, AuditEntriesTable, LoadMore, Loading, shortId } from '@avrahami/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { allTenantsQuery, auditQuery } from '../api/platform';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';

/**
 * The audit trail, newest first, for one tenant or for everything (`tenantId` empty). Loads
 * more on demand; the API bounds every page.
 */
export function AuditTable({ tenantId }: { tenantId: string }) {
  const audit = useInfiniteQuery(auditQuery(tenantId));
  const tenants = useQuery(allTenantsQuery);
  const slugOf = new Map(tenants.data?.items.map((tenant) => [tenant.id, tenant.slug]) ?? []);
  const entries = audit.data?.pages.flatMap((page) => page.items) ?? [];

  if (audit.isPending) return <Loading />;
  if (audit.isError) return <Alert>{errorMessage(audit.error)}</Alert>;
  if (entries.length === 0) return <p className="text-sm text-slate-500">{he.audit.empty}</p>;

  return (
    <>
      <AuditEntriesTable
        entries={entries}
        actionLabels={he.actions}
        words={he.audit}
        extraColumn={
          tenantId === ''
            ? {
                label: he.audit.tenant,
                render: (entry) =>
                  entry.tenantId === null
                    ? he.audit.platform
                    : (slugOf.get(entry.tenantId) ?? shortId(entry.tenantId)),
              }
            : undefined
        }
      />
      <LoadMore
        hasMore={audit.hasNextPage}
        busy={audit.isFetchingNextPage}
        onMore={() => {
          void audit.fetchNextPage();
        }}
      />
    </>
  );
}
